import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import { skillTypeLiteralSchema } from '~/shared/data-contract/filter-response';
import type { FilterWithFilterStates } from '~/shared/data-contract/filter-state';
import {
    createCustomFilterSchema,
    createPreDefinedFilterSchemas,
    createSortFilterStateByOrder,
    customFilterKeySchema,
    customFilterStateSchema,
    departmentFilterStateSchema,
    departmentLiteralSchema,
    getFilterValuesFromFilterValueString,
    jobSeekingFilterStateSchema,
    jobSeekingLiteralSchema,
    jobTitleFilterStateSchema,
    jobTitleLiteralSchema,
    reduceFiltersToKeyValuePairs,
    relocationPreferenceFilterStateSchema,
    relocationPreferenceLiteralSchema,
    seniorityLevelFilterStateSchema,
    seniorityLevelLiteralSchema,
    setFilterStateValuesToFilter,
    skillFilterStateSchema,
} from '~/shared/data-contract/filter-state';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import type { FilterInFilterResponseTransformed } from '../../data-contract/filter-response-transformed';
import { filterResponseTransformedSchema } from '../../data-contract/filter-response-transformed';
import { convertFilterStateToFilters } from '../common/filter-sidebar/utils';

const allowedEmployeeFilterInOrder = [
    skillFilterStateSchema,
    departmentFilterStateSchema,
    jobTitleFilterStateSchema,
    jobSeekingFilterStateSchema,
    relocationPreferenceFilterStateSchema,
    seniorityLevelFilterStateSchema,
] as const;

const filterOrder = allowedEmployeeFilterInOrder.map(({ shape }) => shape.field.value);
const allowedEmployeeFilterFieldNameSchema = z.union([
    departmentLiteralSchema,
    jobTitleLiteralSchema,
    jobSeekingLiteralSchema,
    relocationPreferenceLiteralSchema,
    seniorityLevelLiteralSchema,
    skillTypeLiteralSchema,
]);
const allowedEmployeeAndCustomFilterFieldNameSchema = allowedEmployeeFilterFieldNameSchema.or(customFilterKeySchema);
const allowedEmployeeFilterSchema = z.union(allowedEmployeeFilterInOrder);
const allowedEmployeeAndCustomFilterSchema = allowedEmployeeFilterSchema.or(customFilterStateSchema);

type AllowedEmployeeFilter = z.infer<typeof allowedEmployeeFilterSchema>;

type AllowedEmployeeOrCustomFilter = z.infer<typeof allowedEmployeeAndCustomFilterSchema>;

const sortFilters = createSortFilterStateByOrder({ filterOrder });

const isAllowedOrCustomFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedEmployeeOrCustomFilter =>
    allowedEmployeeAndCustomFilterFieldNameSchema.safeParse(filter.field).success;

const isAllowedFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedEmployeeFilter =>
    allowedEmployeeFilterFieldNameSchema.safeParse(filter.field).success;

const filterQueryParam = 'talentDirectoryEmployeeFilters';

export type UseTalentDirectoryEmployeeFilterResult = {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

export const useTalentDirectoryEmployeeFilter = ({
    enabled,
}: {
    readonly enabled: boolean;
}): UseQueryResult<UseTalentDirectoryEmployeeFilterResult> => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language, locationSearchParams } = applicationState;
    const pathname: Pathname = `/global/directory/users/filter`;

    const resetFilterValues = (): void => {
        applicationState.locationSearchParams.delete(filterQueryParam);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    const saveFilterValues = (updatedFilters: readonly FilterWithFilterStates[]): void => {
        const filterKeyValuePairs = reduceFiltersToKeyValuePairs(updatedFilters);

        if (is.emptyObject(filterKeyValuePairs)) {
            resetFilterValues();
        } else {
            applicationState.locationSearchParams.set(filterQueryParam, JSON.stringify(filterKeyValuePairs));
            actions.setLocationSearchParams(applicationState.locationSearchParams);
        }
    };

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(filterResponseTransformedSchema, {
                pathname,
            });

            if (result.isOk) {
                const allowedEmployeeFilterSchemas = createPreDefinedFilterSchemas({ translate });
                const customEmployeeFilterSchema = createCustomFilterSchema({ attributeNamespace: 'user', translate });
                const filterStates = result.value
                    .filter(isAllowedOrCustomFilter)
                    .sort(sortFilters)
                    .map((filter) => {
                        if (isAllowedFilter(filter)) {
                            const filterResult = allowedEmployeeFilterSchemas[filter.field].safeParse(filter);

                            if (filterResult.success) {
                                return filterResult.data;
                            }
                        }

                        return customEmployeeFilterSchema.parse(filter);
                    });

                return [
                    {
                        id: 'skills',
                        filterStates: filterStates.filter(({ field }) => field === skillTypeLiteralSchema.value),
                        subTitle: translate('pages.learning-opportunities.filters.skills-preferences.sub-title'),
                        title: translate('pages.learning-opportunities.filters.skills-preferences.title'),
                    },
                    {
                        id: 'employee',
                        filterStates: filterStates.filter(({ field }) => field !== skillTypeLiteralSchema.value),
                        subTitle: translate('pages.job-detail.recommendations.drawer.employee-section.sub-headline'),
                        title: translate('pages.job-detail.recommendations.drawer.employee-section.headline'),
                    },
                ];
            }
            throw result.error;
        },
        queryKey: [pathname, 'get', language.code],
        select(filters) {
            const filterValuesJsonString = locationSearchParams.get(filterQueryParam);

            if (is.string(filterValuesJsonString)) {
                const filterValuesFromStorage = getFilterValuesFromFilterValueString({
                    filters,
                    filterValuesJsonString,
                });

                if (filterValuesFromStorage.isOk) {
                    const filtersWithValues = setFilterStateValuesToFilter(filters, filterValuesFromStorage.value);
                    const allFilterStatesWithValues = filtersWithValues.reduce(
                        // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
                        (acc, { filterStates }) => [...acc, ...filterStates],
                        [],
                    );

                    return {
                        filterPayload: Maybe.of(convertFilterStateToFilters(allFilterStatesWithValues)),
                        filters: filtersWithValues,
                        hasActiveFilters: allFilterStatesWithValues.some(({ value }) => !is.nullOrUndefined(value)),
                        resetFilterValues,
                        saveFilterValues,
                    };
                }
            }

            return {
                filterPayload: Maybe.nothing<GeneralFilter>(),
                filters,
                hasActiveFilters: false,
                resetFilterValues,
                saveFilterValues,
            };
        },
    });
};
